import moment from 'moment';

export type RangePreset = 'today' | '7d' | '30d' | 'month';

export const RANGE_PRESETS: {
  key: RangePreset;
  label: string;
  title: string;
}[] = [
  { key: 'today', label: 'Today', title: 'Today' },
  { key: '7d', label: '7d', title: 'Last 7 days' },
  { key: '30d', label: '30d', title: 'Last 30 days' },
  { key: 'month', label: 'Month', title: 'This month' },
];

export const DEFAULT_RANGE: RangePreset = '7d';

// The range the dashboard summary is asked for: a preset, or an explicit inclusive range of
// calendar days. The API resolves either in Toronto time and echoes back the days it used.
export interface SummaryRange {
  preset?: RangePreset;
  from?: string;
  to?: string;
}

const DATE = 'YYYY-MM-DD';
const CUSTOM = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;

const isPreset = (value: string): value is RangePreset =>
  RANGE_PRESETS.some((preset) => preset.key === value);

const isDate = (value: string) => moment(value, DATE, true).isValid();

// ?range= value -> range. Presets are their key; a custom range is 'YYYY-MM-DD..YYYY-MM-DD'.
// Anything unrecognised is the default.
export const parseRange = (raw: string | null | undefined): SummaryRange => {
  const value = (raw ?? '').trim();
  const custom = CUSTOM.exec(value);
  if (custom) {
    const [, from, to] = custom;
    if (isDate(from) && isDate(to) && from <= to) return { from, to };
    return { preset: DEFAULT_RANGE };
  }
  return { preset: isPreset(value) ? value : DEFAULT_RANGE };
};

// range -> ?range= value. '' for the default, so the key drops out of the query string.
export const rangeToParam = (range: SummaryRange): string => {
  if (range.from && range.to) return `${range.from}..${range.to}`;
  return range.preset && range.preset !== DEFAULT_RANGE ? range.preset : '';
};

// range -> the API's query string.
export const rangeQuery = (range: SummaryRange): string => {
  if (range.from && range.to) return `from=${range.from}&to=${range.to}`;
  return `range=${range.preset ?? DEFAULT_RANGE}`;
};

export const rangeTitle = (range: SummaryRange): string => {
  if (range.from && range.to) return 'Custom range';
  return (
    RANGE_PRESETS.find((preset) => preset.key === range.preset)?.title ??
    'Last 7 days'
  );
};

// "+3" / "-2" / "same" against the same-length period before the range.
export const priorDelta = (count: number, prior: number): string => {
  const delta = count - prior;
  if (delta > 0) return `+${delta}`;
  if (delta < 0) return `-${Math.abs(delta)}`;
  return 'same';
};

export const priorLabel = (days: number): string =>
  days === 1 ? 'vs yesterday' : `vs prior ${days} days`;
